import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { authAPI } from "../lib/studentAPI";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Loader2, CheckCircle, XCircle } from "lucide-react";

export const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{
    show: boolean;
    type: "success" | "error";
    message: string;
  }>({ show: false, type: "success", message: "" });

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Auto hide modal after 3 seconds
  useEffect(() => {
    if (modal.show) {
      const timer = setTimeout(() => {
        setModal((prev) => ({ ...prev, show: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [modal.show]);

  const showModal = (type: "success" | "error", message: string) => {
    setModal({ show: true, type, message });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Simple validation via modal
    if (!email.trim()) {
      return showModal("error", "Email is required");
    }

    if (!password) {
      return showModal("error", "Password is required");
    }

    if (password.length < 8) {
      return showModal("error", "Password must be at least 8 characters");
    }

    setLoading(true);

    try {
      const response = await authAPI.login({ email: email.trim(), password });

      login(response.token, response.student);

      showModal("success", "Login successful! Redirecting...");

      // Redirect after modal shows
      setTimeout(() => {
        const redirectTo = (location.state as any)?.from || "/dashboard";
        const enrollCourseId = (location.state as any)?.courseId;

        if (enrollCourseId) {
          navigate("/dashboard", { state: { enrollCourseId } });
        } else {
          navigate(redirectTo);
        }
      }, 3200);
    } catch (error: any) {
      showModal("error", error.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const [showReset, setShowReset] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleResetPassword = async () => {
    if (!email.trim()) {
      return showModal("error", "Enter your email first");
    }

    if (!newPassword || !confirmPassword) {
      return showModal("error", "Fill all fields");
    }

    if (newPassword.length < 8) {
      return showModal("error", "Password must be at least 8 characters");
    }

    if (newPassword !== confirmPassword) {
      return showModal("error", "Passwords do not match");
    }

    try {
      setLoading(true);

      await authAPI.resetPassword({
        email: email.trim(),
        newPassword,
      });

      showModal("success", "Password updated! Please login.");

      // reset UI
      setShowReset(false);
      setNewPassword("");
      setConfirmPassword("");

    } catch (err: any) {
      showModal("error", err.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Back Button */}
        <button
          onClick={() => navigate("/")}
          className="mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </button>

        {/* Login Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-purple-600 mb-4">
              <span className="text-2xl font-bold text-white">LP</span>
            </div>
            <h1 className="text-3xl font-bold mb-2 text-gray-800">Welcome Back</h1>
            <p className="text-gray-600">Login to continue your learning journey</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <Label>Password</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {/* Prominent Green Submit Button - Always Visible */}
            <button
              type="submit"
              disabled={loading}
              className="w-full 
                         bg-green-600 hover:bg-green-700 active:bg-green-800 
                         disabled:bg-green-400 
                         text-white font-semibold text-base
                         py-3.5 px-6 
                         rounded-2xl shadow-lg 
                         transition-all duration-200 
                         flex items-center justify-center gap-2
                         focus:outline-none focus:ring-4 focus:ring-green-300 focus:ring-offset-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Logging in...
                </>
              ) : (
                "Login"
              )}
            </button>
          </form>
            <div className="text-right">
            <button
              type="button"
              onClick={() => setShowReset(true)}
              className="text-sm text-blue-600 hover:underline"
            >
              Forgotten password? Change
            </button>
          </div>
          {showReset && (
            <div className="mt-6 space-y-4 border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-700">
                Reset Password
              </h3>

              <Input
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />

              <Input
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />

              <button
                type="button"
                onClick={handleResetPassword}
                className="w-full bg-blue-600 text-white py-2 rounded-xl"
              >
                Update Password
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <Link to="/register" className="text-green-600 hover:underline font-medium">
                Register
              </Link>
            </p>
          </div>
        </div>
      </motion.div>

      {/* Animated Message Modal (Same as Register) */}
      <AnimatePresence>
        {modal.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ duration: 0.1, ease: "easeOut" }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center"
            >
              <div className="flex justify-center mb-4">
                {modal.type === "success" ? (
                  <CheckCircle className="h-16 w-16 text-green-500" />
                ) : (
                  <XCircle className="h-16 w-16 text-red-500" />
                )}
              </div>

              <h3 className="text-2xl font-semibold mb-2">
                {modal.type === "success" ? "Success!" : "Oops!"}
              </h3>

              <p className="text-gray-600 leading-relaxed">{modal.message}</p>

              {/* Progress bar */}
              <div className="h-1 bg-gray-100 rounded-full mt-6 overflow-hidden">
                <motion.div
                  className="h-full bg-green-500"
                  initial={{ width: "100%" }}
                  animate={{ width: "0%" }}
                  transition={{ duration: 0.5, ease: "linear" }}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};